/**
 * Export a DOM element tree into a PDFMake structure.
 *
 * Given a DOM element, `export(node)` returns a PDFMake structure of the
 * children of the node. The top level `node` is considered as the container and
 * is itself omitted on purpose.
 *
 * Tables are currently unsupported.
 *
 * The following elements have styles for the PDF export, other elements will be
 * displayed as regular text.
 *
 * - <h1>
 * - <h2>
 * - <h3>
 * - <p>
 * - <i>
 * - <b>
 * - <ol>
 * - <li>
 */
export default class DOMExporter {
	/**
	 * Export an HTML string into a PDFMake structure.
	 */
	exportFromHTML(html) {
		const node = document.createElement("div");
		node.innerHTML = html;

		return this.export(node);
	}

	export(node) {
		return {
			content: this._exportChildren(node),
			styles: this._pdfStyles(),
		};
	}

	_exportNode(node) {
		switch (this._getNodeName(node)) {
			case "h1":
			case "h2":
			case "h3":
			case "i":
			case "b": {
				return this._makeAtomicElement(node);
			}
			case "ol": {
				return this._makeOrderedList(node);
			}
			case "ul": {
				return this._makeUnorderedList(node);
			}
			case "img": {
				return this._makeImage(node);
			}
			case "#text": {
				return this._makeTextElement(node);
			}
			default: {
				return this._makeCompositeElement(node);
			}
		}
	}

	_exportChildren(node) {
		// eslint-disable-next-line no-shadow -- bad practice, we should fix this
		return [...node.childNodes].map((node) => this._exportNode(node));
	}

	/**
	 * PDFMake does not support nesting styling of text. We are forced to treat
	 * headings, bold and italic nodes as atomic elements and inline any child
	 * node they contain for the styling to work.
	 */
	_makeAtomicElement(node) {
		return {
			style: `dom-${this._getNodeName(node)}`,
			text: this._getTextContent(node),
		};
	}

	_makeCompositeElement(node) {
		return {
			style: `dom-${this._getNodeName(node)}`,
			text: this._exportChildren(node),
		};
	}

	_makeOrderedList(node) {
		return {
			ol: [...this._exportChildren(node)],
		};
	}

	_makeUnorderedList(node) {
		return {
			ul: [...this._exportChildren(node)],
		};
	}

	_makeImage(node) {
		return {
			image: node.src,
		};
	}

	_makeTextElement(node) {
		return {
			text: this._getTextContent(node),
		};
	}

	_getNodeName(node) {
		return node.nodeName.toLowerCase();
	}

	_getTextContent(node) {
		let text = node.textContent.replace(/\n/g, "");
		if (this._getNodeName(node) === "h2") {
			text = text.toUpperCase();
		}
		return text;
	}

	_pdfStyles() {
		return {
			"dom-h1": {
				fontSize: 20,
				bold: true,
				margin: [0, 20, 0, 20],
			},
			"dom-h2": {
				fontSize: 16,
				bold: true,
				margin: [0, 20, 0, 20],
			},
			"dom-h3": {
				fontSize: 14,
				margin: [0, 20, 0, 4],
			},
			"dom-p": {
				margin: [0, 8],
				alignment: "justify",
			},
			"dom-b": {
				bold: true,
			},
			"dom-i": {
				italics: true,
			},
		};
	}
}
